import { useEffect, useRef } from 'react';
import { IEventData } from 'tools/events';

// Use for CustomApps to set up reference manually
export function buildEmbedUrlManually(embedUrl: string, referenceId: number): string {
    const [path, query = ''] = embedUrl.split('?');

    const currentReferenceMatch = path.match(/\/reference\/(\d+)/);
    const currentReferenceId = currentReferenceMatch ? Number(currentReferenceMatch[1]) : 0;
    if (currentReferenceId === referenceId) {
        return embedUrl;
    }

    const newPath =
        referenceId > 0
            ? /\/reference\/\d+/.test(path)
                ? path.replace(/\/reference\/\d+/, `/reference/${referenceId}`)
                : path.replace(/\/intermediate/, `/reference/${referenceId}/intermediate`)
            : path;

    return query ? `${newPath}?${query}` : newPath;
}

interface NativeFiltersState {
    nativeFilters: string;
    // The pageState returned by the iframe's getFilters reply (object or its JSON string)
    pageState?: unknown;
}

function sendNativeFilters({ nativeFilters, pageState }: NativeFiltersState) {
    const iframe = document.getElementById('iframe-component') as HTMLIFrameElement | null;
    iframe?.contentWindow?.postMessage({ command: 'setFilters', filters: nativeFilters, pageState }, '*');
}

// Listens for the 'mi-element-viewer' setFiltersState event dispatched by CustomApps and either
// swaps the iframe's embedUrl (reference change) or pushes native filters into the already-loaded
// iframe via postMessage. Native filters can only be sent once the intermediate page's content has
// signaled readiness with a 'ready' postMessage (see backend/data/templates/intermediate_pages/powerbi/page_code.html) —
// filters requested before that are buffered and flushed as soon as 'ready' arrives.
export function useExternalReportEmbedMessaging(
    embedUrl: string,
    setEmbedUrl: (embedUrl: string) => void,
    setSelectedReferenceId: (referenceId: number) => void,
) {
    const embedUrlRef = useRef(embedUrl);
    useEffect(() => {
        embedUrlRef.current = embedUrl;
    }, [embedUrl]);

    // A new embedUrl means the iframe reloads from scratch, so any previous 'ready' handshake is void
    const iframeReadyRef = useRef(false);
    const pendingNativeFiltersRef = useRef<NativeFiltersState | null>(null);
    useEffect(() => {
        iframeReadyRef.current = false;
    }, [embedUrl]);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.data?.command !== 'ready') {
                return;
            }
            iframeReadyRef.current = true;
            if (pendingNativeFiltersRef.current) {
                setTimeout(() => {
                    if (pendingNativeFiltersRef.current) {
                        sendNativeFilters(pendingNativeFiltersRef.current);
                        pendingNativeFiltersRef.current = null;
                    }
                }, 1000);
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    useEffect(() => {
        const handler = (e: Event) => {
            if (!(e instanceof CustomEvent) || !e.detail) {
                return;
            }
            const { action, payload } = e.detail as IEventData;
            if (action !== 'setFiltersState') {
                return;
            }
            setSelectedReferenceId(payload.referenceId);
            const newEmbedUrl = buildEmbedUrlManually(embedUrlRef.current, payload.referenceId);
            if (payload.nativeFilters) {
                // Queue regardless of the branch below — if the reference (and so the iframe) is
                // changing, these filters must wait for the reloaded iframe's own 'ready' handshake.
                pendingNativeFiltersRef.current = { nativeFilters: payload.nativeFilters, pageState: payload.pageState };
            }
            if (newEmbedUrl !== embedUrlRef.current) {
                setEmbedUrl(newEmbedUrl);
            } else if (iframeReadyRef.current && pendingNativeFiltersRef.current) {
                sendNativeFilters(pendingNativeFiltersRef.current);
                pendingNativeFiltersRef.current = null;
            }
        };
        document.addEventListener('mi-element-viewer', handler);
        return () => document.removeEventListener('mi-element-viewer', handler);
    }, [setEmbedUrl, setSelectedReferenceId]);
}
